/**
 * STEP TEN — "The gruel is really BRAIN food!!"
 *
 * Step 9 hid the intestines behind the wall. Step 10 hides the LUNGS
 * instead — the heart pumps CO₂ to the right-lower wall (the "send to
 * lungs" port), and oxygenated blood comes back via the right-upper wall
 * (the "from lungs" port). The student wires the digestive + circulatory
 * loop and discovers, by routing the side-output of the intestines into
 * the brain, that the gruel becoming food is what the brain is really
 * craving.
 *
 * Wall ports:
 *   TOP center        spout [gruel]          (esophagus — hint visible)
 *   RIGHT-upper       spout [oxygen] hideContents
 *                     (discovery — narratively "O₂ blood returning from
 *                     off-screen lungs")
 *   RIGHT-lower       receptacle [co2]       (hint visible — sends CO₂
 *                     blood to off-screen lungs for gas exchange)
 *   BOTTOM-right      receptacle [waste]     (toilet)
 *
 * Solution — every cone wired:
 *   TOP gruel → stomach.in → stomach.out (food) → intestines.top (direct link)
 *   heart.A-out (always-on CO₂) → RIGHT-lower (off-screen lungs)
 *   RIGHT-upper (O₂ blood) → heart.B-in
 *   heart.B-out (O₂) → intestines.left-in
 *   intestines.left-out (food + O₂ + CO₂) → brain.A-in
 *   brain.A-out (CO₂) → heart.A-in   ← closes the loop
 *   intestines.bottom (waste) → BOTTOM-right toilet
 */
import {
  rightReceptacle,
  rightSpout,
  toiletReceptacle,
  topSpout,
  type LevelConfig,
} from './portHelpers';

export const LEVEL_10: LevelConfig = {
  step: 10,
  title: 'STEP TEN',
  instructions: 'The gruel is really BRAIN food!!',
  ports: [
    topSpout('s10-gruel-in', 8, ['gruel']),
    // Discovery spout — emits O₂ blood. Narratively "lungs are off-screen
    // somewhere between the two right-wall ports." Fires only after CO₂
    // has arrived at the lower-right receptacle (off-screen lungs need
    // CO₂ in before they return O₂), same wall-port `dependsOn` pattern
    // as Level 6. Heart.A-out is the unconditional kickstart.
    {
      ...rightSpout('s10-o2-blood-in', 6, ['oxygen']),
      hideContents: true,
      dependsOn: ['s10-co2-blood-out'],
    },
    // CO₂ blood receptacle — visible hint. Goes to the off-screen lungs.
    rightReceptacle('s10-co2-blood-out', 12, ['co2']),
    toiletReceptacle('s10-toilet', 17),
  ],
  organPool: [
    // ---------- Heart ----------
    {
      id: 'heart',
      art: 'heart',
      cols: 4,
      rows: 4,
      activatesWhen: ['s10-heart-A-out', 's10-heart-B-out'],
      ports: [
        {
          id: 's10-heart-A-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 0, dRow: -1 },
          carries: ['co2'],
        },
        {
          id: 's10-heart-A-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 0, dRow: 3 },
          carries: ['co2'],
        },
        {
          id: 's10-heart-B-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 3, dRow: -1 },
          carries: ['oxygen'],
        },
        {
          id: 's10-heart-B-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 3, dRow: 3 },
          carries: ['oxygen'],
          dependsOn: ['s10-heart-B-in'],
        },
      ],
    },
    // ---------- Stomach ----------
    {
      id: 'stomach',
      art: 'stomach',
      cols: 3,
      rows: 3,
      activatesWhen: ['s10-stomach-out'],
      ports: [
        {
          id: 's10-stomach-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['gruel'],
        },
        {
          id: 's10-stomach-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 1, dRow: 2 },
          carries: ['food'],
          shape: 'castellated',
          dependsOn: ['s10-stomach-in'],
        },
      ],
    },
    // ---------- Intestines ----------
    {
      id: 'intestines',
      art: 'intestines',
      cols: 4,
      rows: 3,
      activatesWhen: ['s10-int-left-out', 's10-int-bottom'],
      ports: [
        {
          id: 's10-int-top',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['food'],
          acceptsAny: true,
          shape: 'castellated',
        },
        {
          id: 's10-int-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['oxygen'],
          acceptsAny: true,
        },
        {
          id: 's10-int-left-out',
          kind: 'spout',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 1 },
          carries: ['food', 'oxygen', 'co2'],
          transform: (inputs) => {
            const top = inputs.get('s10-int-top') ?? [];
            const sideIn = inputs.get('s10-int-left-in') ?? [];
            const topHasFood = top.includes('food');
            if (sideIn.length === 0 && !topHasFood) return null;
            const out = new Set(sideIn);
            if (topHasFood) {
              out.add('food');
              out.add('co2');
            }
            return Array.from(out);
          },
        },
        {
          id: 's10-int-bottom',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['waste'],
          transform: (inputs) => {
            const top = inputs.get('s10-int-top') ?? [];
            return top.includes('food') ? ['waste'] : null;
          },
        },
      ],
    },
    // ---------- Brain ----------
    // Accepts the intestines' enriched blood directly — the level's
    // "aha" payoff: gruel really is brain food.
    {
      id: 'brain',
      art: 'brain',
      cols: 3,
      rows: 3,
      activatesWhen: ['s10-brain-out'],
      ports: [
        {
          id: 's10-brain-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 1 },
          carries: ['food', 'oxygen', 'co2'],
        },
        {
          id: 's10-brain-out',
          kind: 'spout',
          facing: 'E',
          cellOffset: { dCol: 3, dRow: 1 },
          carries: ['co2'],
          dependsOn: ['s10-brain-in'],
        },
      ],
    },
  ],
};
